import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { EASE } from "./motion";
import { SizeGuide } from "./SizeGuide";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/site";

export const testidPart = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const SizePicker = ({ product, size, setSize }) => (
  <div className="flex flex-wrap gap-1.5">
    {product.variants.map((v) => (
      <button
        key={v.id}
        data-testid={`size-selector-${product.id}-${testidPart(v.title)}`}
        onClick={() => setSize(v)}
        aria-pressed={size?.id === v.id}
        className={`h-9 min-w-9 border px-2.5 font-mono text-[11px] uppercase transition-colors duration-300 ${size?.id === v.id ? "border-cream bg-cream text-ink" : "border-cream/20 text-cream/90 hover:border-cream"}`}
      >
        {v.title}
      </button>
    ))}
  </div>
);

export const ProductCard = ({ product, index, featured }) => {
  const { add } = useCart();
  const [size, setSize] = useState(product.variants.length === 1 ? product.variants[0] : null);
  const href = `/prodotto/${product.slug}`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };

  const onAdd = () => {
    if (!size) {
      toast.error("Scegli prima la taglia", { description: product.name });
      return;
    }
    add(product, size);
    toast.success("Aggiunto al carrello", { description: `${product.name} — taglia ${size.title}` });
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      onMouseMove={onMove}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8% 0px" }}
      transition={{ duration: 1.1, delay: (index % 3) * 0.12, ease: EASE }}
      className={`group clip-notch relative flex flex-col justify-end overflow-hidden bg-[#0c0c0c] ${featured ? "md:col-span-2 lg:row-span-2" : ""}`}
    >
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover brightness-[.8] saturate-[.8] transition-[transform,filter] [transition-duration:1400ms] [transition-timing-function:cubic-bezier(.16,1,.3,1)] group-hover:scale-[1.06] group-hover:brightness-100 group-hover:saturate-100"
      />
      <div className="spotlight absolute inset-0" />
      <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-[#070707] from-30% via-[#070707]/85 to-transparent" />
      <Link to={href} aria-label={`Scopri ${product.name}`} data-testid={`product-card-link-${product.id}`} className="absolute inset-0 z-[1]" />

      <div className="absolute left-5 right-10 top-5 flex items-start justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
        <span className="border border-cream/40 bg-ink px-2 py-1 text-cream">{product.badge}</span>
        <span className="pt-1 text-bone/70">N°{String(index + 1).padStart(2, "0")} · {product.edition}</span>
      </div>

      <div className="relative z-10 p-5 sm:p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/70">{product.tagline}</p>
        <div className="mt-2 flex items-start justify-between gap-4">
          <h3 className={`font-display font-bold uppercase leading-[0.95] tracking-tight ${featured ? "text-3xl sm:text-4xl lg:text-5xl" : "text-xl sm:text-2xl"}`}>
            <Link to={href} className="transition-colors hover:text-acid">{product.name}</Link>
          </h3>
          <span data-testid={`product-price-${product.id}`} className="shrink-0 pt-1 font-mono text-base text-cream">{formatPrice(product.price)}</span>
        </div>
        <p className={`mt-3 text-sm leading-relaxed text-bone/70 ${featured ? "max-w-md sm:text-base" : "line-clamp-2"}`}>{product.description}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          {product.size_guide && <SizeGuide product={product} />}
          <Link to={href} data-testid={`product-details-link-${product.id}`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/80 underline-offset-4 transition-colors hover:text-acid hover:underline">
            Dettagli <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <SizePicker product={product} size={size} setSize={setSize} />
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={onAdd}
            className="inline-flex h-9 items-center gap-2 border border-cream/60 px-4 font-mono text-[11px] uppercase tracking-[0.15em] text-cream transition-[background-color,color,transform] duration-300 hover:bg-cream hover:text-ink active:scale-95"
          >
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </button>
        </div>
      </div>
    </motion.article>
  );
};
